import { TriangleAlert } from 'lucide-react'

export interface AssetListSelectionStatusProps {
	/** Whether the card is selecting - outside the mode there is nothing to say. */
	isSelecting: boolean
	/** What the last download run reported, or null when there is nothing to report. */
	outcome: string | null
	/** Whether that outcome is a failure - nothing at all could be started. */
	isOutcomeFailure: boolean
	/**
	 * What the host says the picking is for, shown until a run reports something. Absent, the
	 * line says the assets are being picked to download, which is what selecting plainly means.
	 */
	hint?: string
}

/**
 * What selecting changed, and what the last download run reported - one line above the grid,
 * where the assets it talks about are, rather than in the heading beside the actions.
 *
 * A run's report replaces the hint instead of stacking under it: once there is an outcome the
 * reader has already done the thing the hint was explaining.
 */
export function AssetListSelectionStatus({
	isSelecting,
	outcome,
	isOutcomeFailure,
	hint = 'Pick the assets to download.',
}: AssetListSelectionStatusProps) {
	if (!isSelecting) return null

	if (!outcome) {
		// Says what the mode changed, since the tiles no longer open on click.
		return <p className="text-ds-sm leading-5 text-ds-text-muted">{hint}</p>
	}

	return (
		<p
			{...(isOutcomeFailure ? { role: 'alert' } : { 'aria-live': 'polite' as const })}
			className={
				isOutcomeFailure
					? 'flex items-center gap-1.5 text-ds-sm leading-5 text-ds-danger'
					: 'flex items-center gap-1.5 text-ds-sm leading-5 text-ds-text-muted'
			}
		>
			{isOutcomeFailure ? <TriangleAlert aria-hidden className="size-4 shrink-0" /> : null}
			{outcome}
		</p>
	)
}
